<template>
  <div>
    <div class="row">
      <div class="col-sm-4 mb-2">
        <button
          v-if="permission.permissions.includes('add')"
          class="btn btn-info btn-sm"
          @click="showModal('create', null)"
        >
          {{ $t('manage-master-data.create') }}
        </button>
      </div>
      <div class="col-sm-4 d-flex align-items-center justify-content-center mb-2">
        <small>{{ $t('pagination.show') }}</small>
        <select
          v-model="pagination.PageSize"
          class="form-control form-control-sm w-auto mx-2"
        >
          <option
            v-for="(size, s) in pageSizes"
            :key="s"
            :value="size"
          >
            {{ size }}
          </option>
        </select>
        <small>{{ $t('pagination.entries') }}</small>
      </div>
    </div>

    <data-table
      ref="table"
      class="my-3"
      :fields="fields"
      :data="companies"
      :detail-row-component="detailRow"
      :detail-row-options="{ fields, permission }"
    >
      <template #action="{ rowData }">
        <a
          v-if="rowData.workLocations.length > 1"
          class="text-decoration-none text-reset"
          @click="toggleRow(rowData)"
        >
          <i
            class="fa fa-plus-circle fa-lg fa-fw"
            :class="{
              'fa-minus-circle': isRowVisible(rowData.id),
              'fa-plus-circle': !isRowVisible(rowData.id)
            }"
          />
        </a>
        <span
          v-else
          class="pl-1 mr-3"
        />

        <a
          v-if="permission.permissions.includes('edit')"
          class="text-decoration-none text-reset ml-3"
          @click="showModal('edit', rowData.id)"
        >
          <img
            src="@/assets/icon/edit.png"
            alt="update"
          >
        </a>
      </template>

      <template #contractors="{ rowData }">
        <div
          v-for="(contractor, c) of rowData.contractors"
          :key="c"
          class="text-nowrap"
        >
          {{ contractor.name }}
        </div>
      </template>

      <template #workLocations="{ rowData }">
        <div
          v-if="rowData.workLocations.length"
          class="d-flex"
        >
          {{ rowData.workLocations[0].name }}
          <a
            v-if="permission.permissions.includes('edit')"
            class="mx-2"
            @click="showModal('editCp', rowData.id, rowData.workLocations[0].id)"
          >
            <img
              src="@/assets/icon/pencil.png"
              alt="edit"
            >
          </a>
        </div>
      </template>

      <template #contact="{ rowData }">
        <div v-if="rowData.workLocations.length">
          <div
            v-for="(contact, c) of rowData.workLocations[0].contactPersons"
            :key="c"
          >
            {{ contact.type }}: {{ contact.name }} ({{ contact.phoneNumber }})
          </div>
        </div>
      </template>

      <template #email="{ rowData }">
        <div v-if="rowData.workLocations.length">
          <div
            v-for="(contact, c) of rowData.workLocations[0].contactPersons"
            :key="c"
          >
            {{ contact.email }}
          </div>
        </div>
      </template>

      <template #siteAdmin="{ rowData }">
        <div v-if="rowData.workLocations.length">
          <div
            v-for="(user, u) of rowData.workLocations[0].users"
            :key="u"
          >
            {{ user.fullname }}
          </div>
        </div>
      </template>
    </data-table>

    <div class="d-flex flex-wrap justify-content-center justify-content-sm-between">
      <div class="mb-2">
        <pagination-info
          :page="companies.currentPage"
          :page-size="companies.pageSize"
          :shown="companies.data.length"
          :total="companies.totalData"
        />
      </div>

      <pagination
        ref="pagination"
        class="mb-2"
        @change="onPageChange"
      />
    </div>

    <modal-add-company
      v-if="modal.create"
      @close="closeModal"
    />

    <modal-edit-company
      v-if="modal.edit"
      :id="modal.id"
      :permission="permission"
      @close="closeModal"
    />

    <modal-edit-cp
      v-if="modal.editCp"
      :id="modal.id"
      :information-id="modal.informationId"
      :permission="permission"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { PAGE_SIZES } from '@/fixtures'
import api from '@/utils/Api'
import DataTable from '@/components/DataTable'
import PaginationInfo from '@/components/PaginationInfo'
import Pagination from '@/components/Pagination'
import MasterDataDetailRow from './MasterDataDetailRow'
import ModalAddCompany from './ModalAddCompany'
import ModalEditCompany from './ModalEditCompany'
import ModalEditCp from './ModalEditCp'

export default {
  components: {
    DataTable,
    PaginationInfo,
    Pagination,
    ModalAddCompany,
    ModalEditCompany,
    ModalEditCp
  },

  data: () => ({
    detailRow: MasterDataDetailRow,
    fields: [
      { name: 'action', title: '', dataClass: '__action' },
      { name: 'name', title: 'Perusahaan', dataClass: '__name' },
      { name: 'contractors', title: 'Kontraktor', dataClass: '__contractor' },
      { name: 'workLocations', title: 'Lokasi Kerja', dataClass: '__location' },
      { name: 'contact', title: 'Contact Person', dataClass: '__contact' },
      { name: 'email', title: 'Notifikasi Email', dataClass: '__email' },
      { name: 'siteAdmin', title: 'Admin Site', dataClass: '__site' }
    ],
    pagination: {
      Page: 1,
      PageSize: 10
    },
    companies: {
      data: []
    },
    modal: {
      id: '',
      informationId: '',
      create: false,
      edit: false,
      editCp: false
    },
    permission: []
  }),

  computed: {
    pageSizes: () => PAGE_SIZES
  },

  watch: {
    'pagination.PageSize' () {
      this.pagination.Page = 1
      this.fetch()
    }
  },

  created () {
    this.getPermission()
    this.fetch()
  },

  methods: {
    getPermission () {
      const dataLoc = JSON.parse(localStorage.getItem('profile'))
      const index = dataLoc.role.permissions.findIndex(element => element.category === 'master_data')
      this.permission = dataLoc.role.permissions[index]
    },
    async fetch () {
      const { data } = await api.companies.list({
        ...this.pagination
      })
      this.companies = data
      this.$refs.pagination.setPaginationData({
        total: data.totalData,
        per_page: data.pageSize,
        current_page: data.currentPage,
        last_page: data.totalPage
      })
    },
    toggleRow (row) {
      this.$refs.table.$children[0].toggleDetailRow(row.id)
    },
    isRowVisible (id) {
      return this.$refs.table.$children[0].isVisibleDetailRow(id)
    },
    onPageChange (page) {
      this.pagination.Page = page
      this.fetch()
    },
    showModal (modalname, dataId, informationId) {
      this.modal.id = dataId
      this.modal.informationId = informationId
      this.modal[modalname] = true
    },
    closeModal (modalname) {
      this.modal.id = null
      this.modal[modalname] = false
      this.fetch()
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep(.vuetable-detail-row > td) {
  padding: 0;
}

::v-deep(.__action) { min-width: 100px; max-width: 100px; }
::v-deep(.__name) { min-width: 200px; max-width: 200px; }
::v-deep(.__contractor) { min-width: 360px; max-width: 360px; }
::v-deep(.__location) { min-width: 300px; max-width: 300px; }
::v-deep(.__contact) { min-width: 300px; max-width: 300px; }
::v-deep(.__email) { min-width: 300px; max-width: 300px; }
::v-deep(.__site) { min-width: 300px; max-width: 300px; }
</style>
